import Hammer from 'hammerjs'
import Helpers from '../modules/Helpers'
import isBrowserIE9 from '../modules/IsBrowserIE9'
import SliderThumbNailGallery from '../modules/SliderThumbnails'

class Slider {

    //#region constructor

        constructor(slider, options = {}) {

            if(!slider) return; // Guard Clause

            // Create an accessible instances of properties
            this.slider = slider;
            
            this.slides = Array.from(this.slider.querySelectorAll('.slider__item'));
            if(this.slides.length === 0) return; // Guard Clause

            this.slidesContainer = this.slider.querySelector('.slides__container');
            this.slidesWrap = this.slider.querySelector('.slider__wrapper');
            this.arrowNavigation = this.slider.querySelectorAll('.slider__toggle'); // I'm not sure why these are called 'toggle' in the dom            
            this.nav = this.slider.querySelectorAll('.slider__nav');

            // Combine passed options and defaults            
            this.options = this.setOptions(options);

            // Set slider General Slider Properties
            this.sliderProperties = this.setSliderProperties();

            // Load the actual slider up
            this.load();

            // If we have a thumbnail set to associate, extend the class to include the functionality
            if (this.isThumbnailClassValid())
                this.thumbNailClass = new SliderThumbNailGallery(this, this.options, this.sliderProperties);
        }

    //#endregion

    //#region Options Settings/Properties

        // Set options: this.options

        setOptions = (options) => {
            this.defaultOptions = {
                autoPlayTime: 3000,
                speed: 0.5,
                mobileSize: 600,
                tabletSize: 800,
                cssPrefixes: ['webkit', 'moz', 'MS', 'o', ''],
                sliderStartPosition: 0,
                navTemplate: '<a href="#">{index}</a>',
                numberOfClones: 6, 
                clonesOffset: 3, 
                slideChangeCallback: () => { },
            }
            return Object.assign(this.defaultOptions, options);
        }

        // Set all slider setting properties: this.sliderProperties
        setSliderProperties = () => {
            return {
                sliderHasThumbnailGallery: this.sliderHasThumbnailGallery(),
                isBrowserIE9: isBrowserIE9(),
                isLoop: this.isLoop(),
                isFullWidth: this.isFullWidth(),
                isLazyLoad: this.isLazyLoad(),
                isAutoPlay: this.isAutoPlay(),
                isSetWidthSlider: this.isSetWidthOfSlider(),
                sliderDisplayAmount: this.getSliderDisplayAmount(),
                loadAllSlidesFunctionUsed: false,
                currentSliderPosition: this.options.sliderStartPosition // inc clones (default is updated when clones are produced)
            }
        }

    //#endregion


    //#region Utility Functions

        // Check if we've navigated to a 'cloned' slider item
        isCurrentSliderAClone = () => this.slides[this.sliderProperties.currentSliderPosition].classList.contains('slider__item--clone');

        // Check if certain slide is a 'cloned' slider item
        isSliderAClone = (index) => this.slides[index].classList.contains('slider__item--clone');

        // Set the slider in/out of the 'loading' state (based on the slider's parent having a loading class, a bit fluffy of a selector, but the idea is that it would wrap everything).
        setLoadingState = loading => loading ? this.slider.parentElement.add('loading') : this.slider.parentElement.classList.remove('loading');

        // Test if the slider has a thumbnail gallery we need to associate with it
        sliderHasThumbnailGallery = () => !!this.slider.dataset.thumbnailId;

        // Get the amount of slides we want to display in the slider area (this needs to be updated on page resize)
        getSliderDisplayAmount = () => this.getSliderDisplayAmount();

        // Set the amount off clones either side of th slider
        setClonesOffset = (offSet) => this.options.clonesOffset = offSet;

        // Set the amount of slides we want to display in the slider area (this needs to be updated on page resize)
        setSliderDisplayAmount = () => this.sliderProperties.sliderDisplayAmount = this.getSliderDisplayAmount();

        // Test is slider is meant to loop
        isLoop = () => !this.setSliderProperties.isBrowserIE9 && this.slider.hasAttribute('data-loop');

        // Test if slider is Full Screen width
        isFullWidth = () => this.slider.hasAttribute('data-full-width');

        // Test the slider itself is set to be lazy loaded into the dom
        isLazyLoad = () => this.slider.hasAttribute('data-lazy');

        // Test if slider is set to autoplay
        isAutoPlay = () => this.slider.hasAttribute('data-autoplay');

        // Set Current Slider Position
        setSliderProperty__CurrentSliderPosition = (position) => this.sliderProperties.currentSliderPosition = position;

        // If we're fixing the width of our sliders rather than leaving responsive
        isSetWidthOfSlider = () => !this.slider.hasAttribute('data-responsive-width');

        // Set width of each slide (no support for portrait)
        setWidthOfSlider = (width) => this.slides.forEach(x => x.style.width = `${width}px`);

        // Remove set width of each slide
        removeWidthOfSlider = (index) => this.slides[index].style.removeProperty("width");

        // Check that we have details an the viewport is the correct size to try and initalise a thumbnail class
        isThumbnailClassValid = () => this.sliderProperties.sliderHasThumbnailGallery && window.innerWidth > this.options.mobileSize;

        // set flag if 'load all images' function has been used, as once used, we don't have to bother again
        setLoadAllSlidesFunctionUsed = (used) => this.sliderProperties.loadAllSlidesFunctionUsed = used;

    //#endregion

    //#region Auto Play

        autoPlay = () => {
            this.autoInt = setInterval(() => {
                this.changeActiveSlider(++this.sliderProperties.currentSliderPosition);
            }, this.options.autoPlayTime)
        }

    //#endregion

    //#region Load Up The Slider

        // Initial Load Up Of The Slider
        load = () => {

            // Add Slider Clones For Looping
            if (this.sliderProperties.isLoop) this.addSlideClones();

            // Set width of each slide based on container
            if(this.sliderProperties.isSetWidthSlider)
                this.setWidthOfSlider(this.slidesWrap.clientWidth /  this.sliderProperties.sliderDisplayAmount);

            // Load Initial images
            if (this.sliderProperties.isLazyLoad) {
                // If we're lazy loading images, ensure we've loaded them before we start calculating widths for positioning etc
                const indices = this.getDefaultSlideIndexes(this.sliderProperties.currentSliderPosition);
                this.lazyLoadSlideImages(indices, true, false, () => {
                    this.sharedLoadFunctions();
                });

            } else {
                this.sharedLoadFunctions();
            }
        }

        // Share general loading functions 
        sharedLoadFunctions = () => {

            // Set slider position directly
            this.setSliderXAxisPosition(this.sliderProperties.currentSliderPosition, false);   

            // Set Initial Slider Classes live
            this.setSliderActiveCSSClasses(this.sliderProperties.currentSliderPosition);

            // Hide/Show arrow nav
            this.hideArrowNavigation();

            // Attach Prev/Next arrow functions
            this.setArrowNavigation();

            // Set swipe gesture
            this.hammer();

            /// Auto play
            if (this.sliderProperties.isAutoPlay) this.autoPlay();

            // Listen for when page is resized
            this.pageResize();

            // Take slider out of 'Loading State'
            this.setLoadingState(false);
        
        }


        // Deferred loading of the slider
        deferredLoad = (s) => {
            return new Promise((resolve) => {
                resolve(new Slider(s))
            })
        }

        // Activate slider 
        activate = () => {
            var that = this

            if (document.readyState === 'complete') {
                this.load() /** 1 */
            } else {
                window.addEventListener('load', function () { that.load() }) /** 1 */
            }
            window.addEventListener('resize', function () { that.pageResize() }) /** 2 */

            this.hammer() /** 3 */


            return this
        }

    //#endregion

    //#region Page resize listener

        // Page resize listener
        pageResize = () => {
            window.addEventListener('resize', () => {
                
                // Thumbnail gallery hasn't been initialised, however it now needs to (going from small to large)
                if(!this.thumbNailClass && this.isThumbnailClassValid())
                    this.thumbNailClass = new SliderThumbNailGallery(this, this.options, this.sliderProperties);

                // Reset how many slide we want on the page to show
                this.setSliderDisplayAmount();

                // Set width of each slide based on container
                if(this.sliderProperties.isSetWidthSlider) this.setWidthOfSlider(this.slidesWrap.clientWidth /  this.sliderProperties.sliderDisplayAmount);

                // Call setSliderXAxisPosition only after all images have finished loading
                this.setSliderXAxisPosition(this.sliderProperties.currentSliderPosition, false);

                // Re-Calc thumbnail position 
                if (this.thumbNailClass) 
                    this.thumbNailClass.setThumbXAxisPosition(this.thumbNailClass.thumbProperties.currentThumbPosition, false);
            });
        }

    //#endregion

    //#region Slider Width

        // Retrieve the number of slides currently visible within the slider display window, such as 3 on desktop, 2 on tablet, and 1 on mobile.
        getSliderDisplayAmount = () => {

            // Guard Clause
            if (!this.sliderHasDisplayQuantitySet())
                return 1;

            const windowWidth = window.innerWidth;
            let attribute;
        
            // Determine which attribute to use based on window width
            switch (true) {
                case windowWidth <= this.options.mobileSize:
                    attribute = 'data-qty-mobile';
                    break;
                case windowWidth <= this.options.tabletSize:
                    attribute = 'data-qty-tablet';
                    break;
                default:
                    attribute = 'data-qty';
            }
        
            return this.getDisplayQuantity(attribute);
        };
        

        // Test if a qty is set
        sliderHasDisplayQuantitySet = () => this.slider.hasAttribute('data-qty') || this.slider.hasAttribute('data-qty-tablet') || this.slider.hasAttribute('data-qty-mobile');

        // Get qty attribute value (default to 1 if not present)
        getDisplayQuantity = (type) => parseInt(this.slider.getAttribute(type) || '1', 10);


    //#endregion

    //#region Set Up Clones

        // Basic entry point for adding clones to slides array and the DOM
        addSlideClones = () => {
            // Clone Last slide(s) and add them to the front of the slides
            this.addFirstSlideClones();

            // Clone First slide(s) and add them to the back of the slides
            this.addLastSlideClones();

            // Update current slider position to take into account clones
            this.setSliderProperty__CurrentSliderPosition(this.options.clonesOffset);
        }


        // Take the last slide(s) and clone them before the first slides array nodes and slide DOM elements 
        addFirstSlideClones = () => {
            const selectedSlides = this.slides.filter(slide => !slide.classList.contains('slider__item--clone'))
                .slice(Math.max(0, this.slides.length - this.options.clonesOffset), this.slides.length);

            // update the default clone offset, if needed (maybe we don't have enough slides to actually reach that number)
            if(selectedSlides.length !== this.options.clonesOffset)
                this.setClonesOffset(selectedSlides.length);

            selectedSlides.reverse().forEach((slide) => {
                const clonedSlide = slide.cloneNode(true);
                // Add clone specific class
                new Helpers(clonedSlide).addClass('slider__item--clone');
                // Add clone to global slides array
                this.slides.unshift(clonedSlide);
                // Add to the DOM
                this.slidesContainer.prepend(clonedSlide);
            });

        };

        // Take the first slide(s) and clone them after the last slides array nodes and slide DOM elements 
        addLastSlideClones = () => {
            const selectedSlides = this.slides.filter(slide => !slide.classList.contains('slider__item--clone'))
                .slice(0, this.options.clonesOffset);
            
            // update the default clone offset, if needed (maybe we don't have enough slides to actually reach that number)
            if(selectedSlides.length !== this.options.clonesOffset)
                this.setClonesOffset(selectedSlides.length);

            selectedSlides.forEach(slide => {
                const clonedSlide = slide.cloneNode(true);
                // Add clone specific class
                new Helpers(clonedSlide).addClass('slider__item--clone');
                // Append clone to global slides array 
                this.slides.push(clonedSlide);
                // Append clone to the DOM
                this.slidesContainer.append(clonedSlide);
            });
        };

    //#endregion

    //#region Prev / Next Button Functions

        // Attach event listeners to arrow buttons
        setArrowNavigation = () => {
            this.arrowNavigation.forEach(button => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();

                    const direction = e.target.getAttribute('data-direction');
                    let newSliderPosition = this.sliderProperties.currentSliderPosition;

                    if(direction === "prev"){
                        newSliderPosition--;
                        // Change active slider
                        this.changeActiveSlider(newSliderPosition);
                        // Change Thumbnail (if present)
                        this.updateNextPreThumbnail(direction);

                    }
                    else { // "next"
                        newSliderPosition++;
                        // Change active slider
                        this.changeActiveSlider(newSliderPosition);
                        // Change Thumbnail (if present)
                        this.updateNextPreThumbnail(direction);
                    }
                });
            });
        }


        // Hide/Show Arrow Navigation at end/start  of slide (If not a loop)
        hideArrowNavigation = () => {

            if(this.slides.length <=this.sliderProperties.sliderDisplayAmount)
            {
                this.arrowNavigation.forEach(x => x.setAttribute('aria-hidden', true));
            } else if (!this.sliderProperties.isLoop) {
                const hidePrev = this.sliderProperties.currentSliderPosition === 0;
                const hideNext = (this.sliderProperties.currentSliderPosition + this.sliderProperties.sliderDisplayAmount) >= this.slides.length;
    
                this.arrowNavigation.forEach((element) => {
                    const direction = element.getAttribute('data-direction');
                    let hide = false;
                    if (direction === 'prev' && hidePrev) {
                        hide = true;
                    } else if (direction === 'next' && hideNext) {
                        hide = true;
                    }
                    element.setAttribute('aria-hidden', hide);
                })
            }        
        }


    //#endregion

    //#region Position change and associated Functions

        // Change where the slider is
        changeActiveSlider = (newSliderPosition, useTransition = true) => {

            // Lazy Load new surrounding images (don't bother waiting, we should be just loading potential next images)
            if (this.sliderProperties.isLazyLoad) {
                const indices = this.getDefaultSlideIndexes(newSliderPosition);
                this.lazyLoadSlideImages(indices, false, true, () => {});
            } 

            // Update CSS
            this.setSliderActiveCSSClasses(newSliderPosition, useTransition); 

            // Trigger the DOM change
            this.setSliderXAxisPosition(newSliderPosition, useTransition); 

            // Hide/Show arrow nav
            this.hideArrowNavigation();

            // Update global position
            this.setSliderProperty__CurrentSliderPosition(newSliderPosition);

            // Check if we need to 'Loop'
            if (this.sliderProperties.isLoop) this.loopSlider(newSliderPosition);
        }

        // update thumbnail next/previous
        updateNextPreThumbnail = (direction) => {

            // If we have a thumbnail gallery update that with the position change
            if (this.thumbNailClass) {
                switch (direction) {
                    case "prev":
                        this.thumbNailClass.previousThumb();
                        break;
                    case "next":
                        this.thumbNailClass.nextThumb();
                        break;
                }
            }
        }



        // Set slider position
        setSliderXAxisPosition = (slideIndex, useTransition = true) => {

            let position = 0;
            let sliced = this.slides.slice(0, slideIndex);

            sliced.forEach(slide => {
                const rect = slide.getBoundingClientRect();
                position += rect.width;
            });

            // if slider is a lot smaller, and responsive, lets try and centre it
            if(!this.sliderProperties.isSetWidthSlider) {
                const slidesViewPortWidth = this.slidesWrap.clientWidth;
                if(this.slides[slideIndex]){
                    const selectedSlideImgWidth = this.slides[slideIndex].clientWidth;
                    if(selectedSlideImgWidth < slidesViewPortWidth) {
                        const adjustAmount = (slidesViewPortWidth - selectedSlideImgWidth) / 2;
                        position = position - adjustAmount;
                    }
                }
            }

            // If slider is gracefully 'sliding' to new position or jumping (Jump to, probably part of a 'loop')
            if (useTransition) {
                this.slidesContainer.style.transition = `transform ${this.options.speed}s ease-in-out 0s`;
            } else {
                this.slidesContainer.style.transition = 'none';
            }

            // Move slider into position
            if (this.sliderProperties.isBrowserIE9) {
                this.slidesContainer.style.msTransform = `translateX(-${position}px)`
            } else {
                this.slidesContainer.style.transform = `translate3d(-${position}px, 0px, 0px)`
                this.slidesContainer.style.webkitTransform = `translateX(-${position}px)`
            }
        }

        // Set slider as active via class
        // Note: we use 'active--no-transition' to avoid buggy double transitions while looping the slider
        setSliderActiveCSSClasses = (position, useTransition = true) => {
            this.slides.forEach(x => x.classList.remove('active', 'active--no-transition'));
            const slide = this.slides[position];
            useTransition ? slide.classList.add('active') : slide.classList.add('active--no-transition');
        }

    //#endregion

    //#region Looping

        // Check and then loop the slider if nee be
        loopSlider = (newSliderPosition) => {

            const offset = this.options.clonesOffset;
            
            if (this.isSliderAClone(newSliderPosition)) {
                if (newSliderPosition < offset) {
                    let adjustedNewPosition = newSliderPosition + (this.slides.length - (offset * 2));
                    this.doBackLoop(adjustedNewPosition, false);
                }

                else if (newSliderPosition >= this.slides.length - offset) {
                    let adjustedNewPosition = newSliderPosition - (this.slides.length - offset) + offset;
                    this.doFrontLoop(adjustedNewPosition, false);
                }
            }
        }


        // Loop back over images (so from the first slider to the last)
        doBackLoop = (newSliderPosition, useTransition) => {
            setTimeout(() => {
                if (this.sliderProperties.isLazyLoad)
                {
                                        
                    const indices = this.getDefaultSlideIndexes(newSliderPosition);
                    this.lazyLoadSlideImages(indices, false, true, () => {
                        this.loopFunctions(newSliderPosition, useTransition);
                        setTimeout(() => {
                            // If we're going backwards through the loop we're going to have a bad time if we don't load the backwards slide images
                            this.loadAllSlides();
                        }, this.options.speed * 1000);
                    });
                    
                }
                else
                    this.loopFunctions(newSliderPosition, useTransition);
            }, this.options.speed * 1000);
        }


        // Loop back over images (so from the first slider to the last)
        doFrontLoop = (newSliderPosition, useTransition) => {
            setTimeout(() => {
                if (this.sliderProperties.isLazyLoad)
                {
                    const indices = this.getDefaultSlideIndexes(newSliderPosition);
                    this.lazyLoadSlideImages(indices, false, true, () => this.loopFunctions(newSliderPosition, useTransition));
                }   
                else
                    this.loopFunctions(newSliderPosition, useTransition);
            }, this.options.speed * 1000);
        }


        // Shared loop functionality regardless of front or back loop 
        loopFunctions = (newSliderPosition, useTransition) => {

            // Trigger the DOM change
            this.setSliderXAxisPosition(newSliderPosition, useTransition);

            // Update CSS
            this.setSliderActiveCSSClasses(newSliderPosition, useTransition);

            // Update global position
            this.setSliderProperty__CurrentSliderPosition(newSliderPosition);
        }

    //#endregion

    //#region Images

        // Get the next/prev indexes of images to load
        getDefaultSlideIndexes = (newSliderPosition) => {

            const slideIndexesToLoad = new Set();
            const loadThreshold = this.sliderProperties.sliderDisplayAmount;
        
            // Load previous slide indexes
            for (let i = newSliderPosition - 1; i >= newSliderPosition - loadThreshold && i >= 0; i--) {
                slideIndexesToLoad.add(i);
            }
        
            // Load next slide indexes
            for (let i = newSliderPosition; i <= newSliderPosition + loadThreshold && i < this.slides.length; i++) {
                slideIndexesToLoad.add(i);
            }

            return slideIndexesToLoad;
        }

        // If we're dealing with a 'responsive' non-fixed width slider,
        // we're going to have a really bad time if the images and therefore widths pre-ceeding haven't been loaded in yet. 
        // So, unfortunately, we have to load them all.
        loadAllSlides = () => {
            if (!this.sliderProperties.isSetWidthSlider && !this.sliderProperties.loadAllSlidesFunctionUsed) {
                const allIndexes = Array.from({ length: this.slides.length }, (_, index) => index);
                this.lazyLoadSlideImages(allIndexes, false, false, () => { });
                // flag that all the images have been loaded, so we don't bother trying again
                this.setLoadAllSlidesFunctionUsed(true);
            }
        }

        // lazy load images
        lazyLoadSlideImages = (slideIndexesToLoad, forcePositionRecalculation, useTransition, callback) => {

            let imagesLoadedCount = 1; // it's a 'size' set, not an array
            const totalImagesToLoad = slideIndexesToLoad.size;
        
            const checkCallback = () => {

                if (forcePositionRecalculation) {
                    this.setSliderXAxisPosition(this.sliderProperties.currentSliderPosition, useTransition);
                }
                if (imagesLoadedCount == totalImagesToLoad) {
                    callback();
                }
            };
        
            slideIndexesToLoad.forEach((index) => {
                const imagesToLoad = this.slides[index].querySelectorAll('img[data-src]');
        
                if (imagesToLoad.length === 0) {
                    imagesLoadedCount++;
                    checkCallback();
                } else {
                    imagesToLoad.forEach(img => {
                        if (!img.classList.contains('loaded')) {
                            const src = img.getAttribute('data-src');
                            img.src = src;
                            img.onload = () => {
                                if(!this.sliderProperties.isSetWidthSlider) this.removeWidthOfSlider(index);
                                new Helpers(img).addClass('loaded');
                                imagesLoadedCount++;
                                checkCallback();
                            };
                        } else {
                            if(!this.sliderProperties.isSetWidthSlider) this.removeWidthOfSlider(index);
                            imagesLoadedCount++;
                            checkCallback();
                        }
                    });
                }
            });
        };
        
    //#endregion

    //#endregion hammer / swipe gesture

        hammer = () => {
            if (typeof Hammer === 'function') {

                const sliderHammertime = new Hammer(this.slidesContainer);

                sliderHammertime.on('panright', () => {
                    this.hammerDirection = 'prev';
                });

                sliderHammertime.on('panleft', () => {
                    this.hammerDirection = 'next';
                });

                sliderHammertime.on('panend', () => {

                    let newSliderPosition = this.hammerDirection === 'next' ? ++this.sliderProperties.currentSliderPosition : --this.sliderProperties.currentSliderPosition;

                    // Update slider
                    this.changeActiveSlider(newSliderPosition);

                    /// Update thumbs
                    this.updateNextPreThumbnail(this.hammerDirection);
                    
                    clearInterval(this.autoInt);
                });
            }
        }

    //#endregion

}

export default Slider;